/* 
 * In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
 * NEXORAX SYSTEM NODE-12.7: CHIEF SYSTEMS OPERATIONAL & GRAPHICS DIRECTIVES
 */

import React, { useState, useEffect, Component } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Activity, Cpu, Database, Globe, RefreshCw, Zap, TrendingUp, Users, Sparkles, 
  ChevronRight, ArrowUpRight, Play, Pause, Video, FileText, Download, Layers, ShieldAlert,
  Terminal, ShieldCheck, Mail, Key, Sliders, ExternalLink, HelpCircle,
  Search, MessageSquare, ShoppingBag, FolderKanban, Server, Scale, Send
} from "lucide-react";
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, 
  BarChart, Bar, RadialBarChart, RadialBar, Legend, CartesianGrid 
} from "recharts";
import { useToast } from "../contexts/ToastContext";
import { NexusAgents } from "./NexusAgents";
import { NexusGalaxyLab } from "./NexusGalaxyLab";
import { NexusMailDomains } from "./NexusMailDomains";
import { NexusSearch } from "./NexusSearch";
import { NexusHosting } from "./NexusHosting";
import { NexusMarketplace } from "./NexusMarketplace";
import { NexusWorkSuite } from "./NexusWorkSuite";
import { NexusChat } from "./NexusChat";
import { NexusMediaAdSocial } from "./NexusMediaAdSocial";
import { NexusOrganicGrowthEngine } from "./NexusOrganicGrowthEngine";
import { NexoraxHMRCXMLExporter } from "./NexoraxHMRCXMLExporter";
import { NexusUser, WorkSuiteTask, SocialPost } from "../types/nexus";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackModuleName: string;
  onReset?: () => void;
  key?: any;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: any;
}

class ConsoleErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null, errorInfo: null };
  props: ErrorBoundaryProps;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.props = props;
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Advanced Console Module Crash:", error, errorInfo);
    (this as any).setState({ error, errorInfo });
  }

  handleRestart = () => {
    (this as any).setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div id="console-error-boundary-view" className="w-full p-6 border border-rose-500/30 rounded-2xl bg-black/80 backdrop-blur-md text-slate-200 font-mono space-y-4">
          <div className="flex items-center justify-between border-b border-rose-500/20 pb-3">
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
              <h3 className="text-xs font-black uppercase text-rose-400 tracking-wider">
                NEXORAX CRITICAL ISOLATION BADGE: {this.props.fallbackModuleName} CRASHED
              </h3>
            </div>
            <span className="text-[9px] text-rose-500 font-bold bg-rose-500/10 px-2 py-0.5 rounded border border-rose-500/25">
              CONTAINED (NODE-09.2)
            </span>
          </div>

          <div className="text-[11px] leading-relaxed text-slate-300">
            A fatal initialization or state rendering exception occurred inside the active <span className="text-yellow-400 font-semibold">{this.props.fallbackModuleName}</span> segment container. The NexoraX Execution Core has safely quarantined the subsystem to prevent whole-OS terminal failures.
          </div>

          <div className="p-4 bg-zinc-950 border border-zinc-900 rounded-xl space-y-2 max-h-[180px] overflow-auto">
            <div className="text-[10px] text-rose-400 font-bold">
              EXCEPTION STACKLOG:
            </div>
            <div className="text-[9px] text-zinc-400 font-mono break-all leading-normal">
              {this.state.error?.toString()}
            </div>
            {this.state.errorInfo && (
              <div className="text-[8.5px] text-zinc-500 font-mono break-all leading-normal whitespace-pre">
                {this.state.errorInfo.componentStack?.trim()}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-1">
            <div className="text-[9px] text-zinc-500">
              COGNITIVE WEIGHTS SHIELDED • NexoraX Resource Library SAFE • LATENCY STABLE [0.8S]
            </div>
            <button
              id="reset-module-subsystem"
              onClick={this.handleRestart}
              className="flex items-center gap-1.5 px-4 py-1.5 border border-rose-500 bg-rose-500/10 hover:bg-rose-500/25 text-rose-300 font-bold text-[9px] uppercase rounded-lg transition-all duration-200 cursor-pointer"
            >
              <RefreshCw size={10} className="animate-spin text-rose-300" style={{ animationDuration: "4s" }} />
              <span>Remount Subsystem & Purge Memory</span>
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface NexoraxAdvancedConsoleProps {
  initialSubTab?: string;
}

export function NexoraxAdvancedConsole({ initialSubTab }: NexoraxAdvancedConsoleProps = {}) {
  const verifiedPraise = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";
  const { triggerToast } = useToast();

  // SECURE LEDGER ACCREDITATIONS
  const ledgerAccreditation = {
    sortCode: "04-**-**",
    accountNum: "******15",
    bank: "Secure Payment Provider",
    licence: "SC887095",
    SecurePaymentProviderGrant: "NI NH******B Linked",
    supportEmail: "support@nexoraxdigital.co.uk"
  };

  // Performance Learning State - Live Feeding
  const [bestHook, setBestHook] = useState("");
  const [bestAvatar, setBestAvatar] = useState("");
  const [bestDestination, setBestDestination] = useState("");
  const [bestVideoLength, setBestVideoLength] = useState("");
  const [bestCta, setBestCta] = useState("");
  const [bestLanguage, setBestLanguage] = useState("");
  const [bestAudience, setBestAudience] = useState("");
  const [bestVisual, setBestVisual] = useState("");

  // Real-Time Ad Factory Generation KPIs Tracked state
  const [totalCampaignsGen, setTotalCampaignsGen] = useState(14);
  const [totalUgcVideos, setTotalUgcVideos] = useState(24);
  const [totalHooks, setTotalHooks] = useState(48);
  const [totalCreatives, setTotalCreatives] = useState(19);
  const [totalAbVariations, setTotalAbVariations] = useState(35);
  const [totalPublished, setTotalPublished] = useState(8);
  const [lastPublishedChan, setLastPublishedChan] = useState("LinkedIn");
  const [factoryAvgCtr, setFactoryAvgCtr] = useState(5.42);

  // Function to pull live performance parameters directly from Ad Factory database structures
  const loadLivePerformanceMetrics = () => {
    // Seed initial metrics if not present to ensure robust board presentation on first load
    if (!localStorage.getItem("nx_total_campaigns_generated")) {
      localStorage.setItem("nx_total_campaigns_generated", "14");
      localStorage.setItem("nx_total_ugc_videos_rendered", "24");
      localStorage.setItem("nx_total_hooks_created", "48");
      localStorage.setItem("nx_total_creatives_crafted", "19");
      localStorage.setItem("nx_total_ab_variations", "35");
      localStorage.setItem("nx_total_campaigns_published", "8");
      localStorage.setItem("nx_last_published_platform", "LinkedIn");
      localStorage.setItem("nx_ad_factory_avg_ctr", "5.42");
    }

    setBestHook(localStorage.getItem("nx_best_hook") || "“Save 10 Hours Per Week With AI”");
    setBestAvatar(localStorage.getItem("nx_best_avatar") || "Eleanor Vance (94% CTR calibration)");
    setBestDestination(localStorage.getItem("nx_best_destination") || "LinkedIn Sponsored Ad Spot");
    setBestVideoLength(localStorage.getItem("nx_best_vlength") || "15s Vertical Aspect");
    setBestCta(localStorage.getItem("nx_best_cta") || "“Schedule Candidate Technical Interview”");
    setBestLanguage(localStorage.getItem("nx_best_lang") || "English Baseline & Arabic Localised");
    setBestAudience(localStorage.getItem("nx_best_audience") || "Venture Recruiters & Private Secretaries");
    setBestVisual(localStorage.getItem("nx_best_visual") || "Obsidian Glass with Glowing Guide lines");

    // Fetch live counts from Ad Factory
    setTotalCampaignsGen(parseInt(localStorage.getItem("nx_total_campaigns_generated") || "14", 10));
    setTotalUgcVideos(parseInt(localStorage.getItem("nx_total_ugc_videos_rendered") || "24", 10));
    setTotalHooks(parseInt(localStorage.getItem("nx_total_hooks_created") || "48", 10));
    setTotalCreatives(parseInt(localStorage.getItem("nx_total_creatives_crafted") || "19", 10));
    setTotalAbVariations(parseInt(localStorage.getItem("nx_total_ab_variations") || "35", 10));
    setTotalPublished(parseInt(localStorage.getItem("nx_total_campaigns_published") || "8", 10));
    setLastPublishedChan(localStorage.getItem("nx_last_published_platform") || "LinkedIn");
    setFactoryAvgCtr(parseFloat(localStorage.getItem("nx_ad_factory_avg_ctr") || "5.42"));
  };

  useEffect(() => {
    loadLivePerformanceMetrics();
    // Refresh stats every 3 seconds to ensure direct real-time feeding
    const interval = setInterval(loadLivePerformanceMetrics, 3000);
    return () => clearInterval(interval);
  }, []);

  // System State Levers
  const [selectedPreset, setSelectedPreset] = useState("Elite Talent Acquisition");
  const [productName, setProductName] = useState("NexoraX Principal Private Secretary Selection");
  const [productOffer, setProductOffer] = useState("Zero placement fee. Remuneration fully covered via NexoraX billing records (NI NH******B) under SC887095.");
  const [targetAudience, setTargetAudience] = useState("NexoraX Client Resource Workflow Agencies & Board Directors");
  const [campaignGoal, setCampaignGoal] = useState("Rapid candidate interview scheduling");
  const [campaignCta, setCampaignCta] = useState("Call direct operating hotline: 07359******");
  const [selectedPlatform, setSelectedPlatform] = useState("LinkedIn");
  const [activeAdNode, setActiveAdNode] = useState("Node 01");

  // Advanced Console Sub-Tabs Navigation
  type ConsoleSubTab = 
    | 'CAMPAIGN_AND_TELEMETRY' 
    | 'AI_AGENT_AUTOMATION' 
    | 'GALAXY_LAB' 
    | 'MAIL_DOMAINS'
    | 'NEXUS_SEARCH'
    | 'NEXUS_HOSTING'
    | 'MARKETPLACE'
    | 'WORK_SUITE'
    | 'NEXUS_CHAT'
    | 'MEDIA_AD_SOCIAL'
    | 'ORGANIC_GROWTH'
    | 'COMPLIANCE_AND_MANDATES';

  const [activeSubTab, setActiveSubTab] = useState<ConsoleSubTab>(() => {
    if (initialSubTab) {
      const normalized = initialSubTab.toUpperCase().replace(/\s+/g, '_');
      if ([
        'CAMPAIGN_AND_TELEMETRY', 'AI_AGENT_AUTOMATION', 'GALAXY_LAB', 'MAIL_DOMAINS',
        'NEXUS_SEARCH', 'NEXUS_HOSTING', 'MARKETPLACE', 'WORK_SUITE',
        'NEXUS_CHAT', 'MEDIA_AD_SOCIAL', 'ORGANIC_GROWTH', 'COMPLIANCE_AND_MANDATES'
      ].includes(normalized)) {
        return normalized as ConsoleSubTab;
      }
    }
    return 'CAMPAIGN_AND_TELEMETRY';
  });

  const [socialPosts, setSocialPosts] = useState<SocialPost[]>([
    { id: "post-001", platform: "LinkedIn", caption: "NexoraX Enterprise Secure Invoice Register acquisition nodes online.", status: "Approved" }
  ]);

  const [user, setUser] = useState<NexusUser>({
    id: "usr-27",
    email: "siddique.mahmood@gmail.com",
    workspaceId: "27-NexoraX-Grid",
    plan: 'Platinum',
    billingStatus: 'Active',
    searchCredits: 12500,
    aiTasksRemaining: 940,
    rendersRemaining: 480
  });

  const [workSuiteTasks, setWorkSuiteTasks] = useState<WorkSuiteTask[]>([
    { id: "task-001", title: "Route net finances from SC887095 to Secure Payment Provider (04-06-05)", project: "Financial Recon", client: "Director Office", priority: "URGENT", status: "TODO" },
    { id: "task-002", title: "Process compliance applications for remote node Staff Coordinator", project: "Enterprise Standards Sync", client: "Director Office", priority: "HIGH", status: "IN_PROGRESS" }
  ]);

  // Synthetic Core Variables
  const [generationLoading, setGenerationLoading] = useState(false);
  const [generatedAdvCampaign, setGeneratedAdvCampaign] = useState<any>(null);

  // NexoraX Compliance Sub-tab States
  const [selectedSecRegister, setSelectedSecRegister] = useState<'Eleanor Vance' | 'Helen' | 'Claire'>('Eleanor Vance');
  const [approvedSubsidyPct, setApprovedSubsidyPct] = useState(100);
  const [placementFeePct, setPlacementFeePct] = useState(15);
  const [xmlPayloadText, setXmlPayloadText] = useState(() => {
    return `<?xml version="1.0" encoding="UTF-8"?>
<GovTalkMessage xmlns="http://govtalk.gov.uk">
  <EnvelopeVersion>2.4</EnvelopeVersion>
  <Header>
    <MessageDetails>
      <Class>HMRC-VAT-DEC</Class>
      <Qualifier>request</Qualifier>
      <TransactionID>NEXORAX_MANDATE_XML_v10</TransactionID>
    </MessageDetails>
    <SenderDetails>
      <IDAuthentication>
        <SenderID>NEXORAX_DIGITAL_MTD</SenderID>
        <Authentication>
          <Method>MD5</Method>
          <Role>principal</Role>
        </Authentication>
      </IDAuthentication>
    </SenderDetails>
  </Header>
  <GovTalkDetails>
    <Keys>
      <Key Type="CompanyRegNumber">SC887095</Key>
    </Keys>
  </GovTalkDetails>
  <Body>
    <IRenvelope xmlns="http://govtalk.gov.uk">
      <VATDeclaration>
        <Box1>2400.00</Box1>
        <Box2>0.00</Box2>
        <Box3>2400.00</Box3>
        <Box4>90.00</Box4>
        <Box5>2310.00</Box5>
        <Box6>12000</Box6>
        <Box7>450</Box7>
        <Box8>0</Box8>
        <Box9>0</Box9>
      </VATDeclaration>
    </IRenvelope>
  </Body>
</GovTalkMessage>`;
  });
  const [isTransmittingHMRC, setIsTransmittingHMRC] = useState(false);
  const [transmitSteps, setTransmitSteps] = useState<string[]>([]);
  const [transmitStepIdx, setTransmitStepIdx] = useState(-1);
  const [schemeApproved, setSchemeApproved] = useState(true);
  const [logs, setLogs] = useState<string[]>([
    `[${new Date().toLocaleTimeString()}] COMMAND NODE: NexoraX Advanced Console initialized under SC887095.`,
    `[${new Date().toLocaleTimeString()}] AUDIT_SEC: Verified Secure Payment Provider ledger synchronizer: 04-**-**.`
  ]);

  const addLog = (text: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [`[${timestamp}] ${text}`, ...prev.slice(0, 40)]);
  };

  const handleAddLog = (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => {
    addLog(`${module.toUpperCase()}: ${action} (${status}) - ${details}`);
  };

  const handleDeductTasks = (amt: number) => {
    setUser(prev => ({ ...prev, aiTasksRemaining: Math.max(0, prev.aiTasksRemaining - amt) }));
  };

  // Re-triggering manual synchronization from local storage
  const handleForceSync = () => {
    loadLivePerformanceMetrics();
    addLog("SYNCHRONIZER: Forcibly synced learning metrics block from Ad Studio database.");
    triggerToast("Performance learning matrix synced successfully!", "success");
  };

  const handleResetFactoryMetrics = () => {
    localStorage.removeItem("nx_total_campaigns_generated");
    localStorage.removeItem("nx_total_ugc_videos_rendered");
    localStorage.removeItem("nx_total_hooks_created");
    localStorage.removeItem("nx_total_creatives_crafted");
    localStorage.removeItem("nx_total_ab_variations");
    localStorage.removeItem("nx_total_campaigns_published");
    localStorage.removeItem("nx_last_published_platform");
    localStorage.removeItem("nx_ad_factory_avg_ctr");
    loadLivePerformanceMetrics();
    addLog("CALIBRATOR: Resetted Ad Factory generation metrics state back to baseline.");
    triggerToast("Ad Factory telemetry metrics calibrated successfully!", "info");
  };

  // Fast synthesis of mirrored campaign components in the advanced console
  const handleSynthesizeAdvancedAd = () => {
    setGenerationLoading(true);
    addLog(`MATRIX: Re-routing parameters to process "${productName}" campaign at 0.8s SLA latency.`);
    
    setTimeout(() => {
      setGeneratedAdvCampaign({
        header: `NEXORAX MANDATE: Zero-Cost ${productName} Deployment`,
        body: `Office of the Director presents: ${productOffer}. High-fidelity calibration, instant on-grid access, 100% covered.`,
        tags: ["#Nexorax", "#AccessToWork", "#SaaSAutopilot", "#Resourceresource"],
        performanceScore: (Math.random() * 5 + 93).toFixed(2) + "%",
        ctrEstimate: (Math.random() * 2 + 5.1).toFixed(2) + "%",
        monthlyLeads: Math.floor(Math.random() * 200 + 450) + " Leads"
      });
      setGenerationLoading(false);
      addLog("MATRIX: Realized and verified custom ad parameters mirroring AI Ad Factory.");
      triggerToast("Advanced Ad parameters synthesized!", "success");
    }, 850);
  };

  // Node telemetry models
  const initialNodes = [
    { id: "Node 01", name: "Vanguard-Closer", role: "WhatsApp Outreach", status: "Active Thread", loading: 1.2, performance: 94 },
    { id: "Node 06", name: "The Oracle", role: "resource Validation", status: "Optimal Sweep", loading: 0.8, performance: 91 },
    { id: "Node 16", name: "The Gavel", role: "NexoraX Compliance Check", status: "Secure Standby", loading: 2.1, performance: 98 },
    { id: "Node 17", name: "Chronos Ledger", role: "SecurePayment Financial Swipes", status: "Continuous Flow", loading: 0.5, performance: 96 }
  ];

  const [ecosystemNodes, setEcosystemNodes] = useState(initialNodes);

  // Dynamic simulation values driven by interactive learning
  const chartData = [
    { name: "02:00", ctr: 4.25, leads: 120, satisfaction: 88, CPC: 1.35 },
    { name: "06:00", ctr: 4.88, leads: 180, satisfaction: 91, CPC: 1.12 },
    { name: "10:00", ctr: 5.15, leads: 240, satisfaction: 94, CPC: 0.98 },
    { name: "14:00", ctr: 5.32, leads: 390, satisfaction: 96, CPC: 0.86 },
    { name: "18:00", ctr: 5.61, leads: 420, satisfaction: 98, CPC: 0.74 },
    { name: "22:00", ctr: 5.42, leads: 380, satisfaction: 99, CPC: 0.80 },
  ];

  return (
    <div className="space-y-6 text-zinc-100 max-w-full overflow-x-hidden font-sans pb-16">
      
      {/* HEADER BILLING ACCREDITATIONS & MANDATORY PREAMBLE */}
      <div className="bg-gradient-to-r from-purple-950/20 via-black to-cyan-950/20 border-b border-purple-500/10 p-5 -mx-4 md:-mx-8">
        <div className="max-w-[1500px] mx-auto space-y-2">
          {/* Mandatory Arabic/English praise header sequence */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 pb-2 border-b border-white/5">
            <span className="text-[10px] md:text-xs font-mono font-bold tracking-wide text-zinc-400 font-extrabold flex items-center gap-1.5 leading-normal">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping" />
              {verifiedPraise}
            </span>
            <span className="text-[8.5px] font-mono bg-purple-900/30 px-3 py-1 rounded text-[#e840f3] border border-purple-500/10 uppercase tracking-widest font-extrabold text-right">
              Console SLA Unit Latency: 0.8s Baseline
            </span>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center pt-2 gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase font-bold tracking-widest text-[#00e5ff] font-mono">NODE-12.7 SECURE MANAGEMENT VIEW</span>
                <span className="text-[8px] bg-emerald-950/60 text-emerald-400 px-2 rounded-md font-sans border border-emerald-500/10 uppercase font-black tracking-widest">NEXORAX TRUST</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-sans font-black uppercase tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-gray-100 via-zinc-200 to-gray-400">
                NexoraX Systems Advanced Console
              </h1>
            </div>

            {/* Quick Ledger Accred */}
            <div className="bg-black/60 border border-white/5 p-2 px-3.5 rounded-xl font-mono text-[9.5px] leading-relaxed flex flex-wrap gap-x-4 gap-y-1">
              <div>
                <span className="text-zinc-500 uppercase block text-[7.5px]">LEDGER DESTINATION</span>
                <span className="text-yellow-400 font-bold">{ledgerAccreditation.bank} • {ledgerAccreditation.sortCode}</span>
              </div>
              <div>
                <span className="text-zinc-500 uppercase block text-[7.5px]">SPONSORSHIP LICENSE</span>
                <span className="text-[#00e5ff] font-bold">{ledgerAccreditation.licence}</span>
              </div>
              <div>
                <span className="text-zinc-500 uppercase block text-[7.5px]">Secure Payment Provider SUBSIDY LINK</span>
                <span className="text-pink-400 font-bold">{ledgerAccreditation.SecurePaymentProviderGrant}</span>
              </div>
            </div>
          </div>

          {/* Sub-Category Tabs inside the Console Header Container */}
          <div className="flex flex-wrap gap-2 pt-4 mt-3 border-t border-white/5">
            {[
              { id: 'CAMPAIGN_AND_TELEMETRY', label: 'Campaign Compiler & Telemetry', icon: Sliders, color: 'text-cyan-400' },
              { id: 'AI_AGENT_AUTOMATION', label: 'NexoraX AI Agent Automation', icon: Cpu, color: 'text-[#e840f3]' },
              { id: 'GALAXY_LAB', label: 'Nexus Galaxy Lab', icon: Sparkles, color: 'text-yellow-400' },
              { id: 'MAIL_DOMAINS', label: 'Mail & Domains Platform', icon: Mail, color: 'text-emerald-400' },
              { id: 'NEXUS_SEARCH', label: 'Nexus Search', icon: Search, color: 'text-blue-400' },
              { id: 'NEXUS_HOSTING', label: 'Nexus Hosting', icon: Server, color: 'text-rose-400' },
              { id: 'MARKETPLACE', label: 'Marketplace', icon: ShoppingBag, color: 'text-amber-500' },
              { id: 'WORK_SUITE', label: 'WorkSuite', icon: FolderKanban, color: 'text-purple-400' },
              { id: 'NEXUS_CHAT', label: 'Nexus Chat', icon: MessageSquare, color: 'text-indigo-400' },
              { id: 'MEDIA_AD_SOCIAL', label: 'Media/Ad/Social', icon: Video, color: 'text-pink-500' },
              { id: 'ORGANIC_GROWTH', label: 'Organic Growth', icon: TrendingUp, color: 'text-teal-400' },
              { id: 'COMPLIANCE_AND_MANDATES', label: 'Compliance & Mandates Hub', icon: Scale, color: 'text-amber-400' }
            ].map((subTab) => {
              const isSubActive = activeSubTab === subTab.id;
              const Icon = subTab.icon;
              return (
                <button
                  key={subTab.id}
                  id={`subtab-${subTab.id.toLowerCase()}`}
                  onClick={() => {
                    setActiveSubTab(subTab.id as any);
                    addLog(`INTERFACE: Switched Advanced Console category tab to "${subTab.label.toUpperCase()}".`);
                    triggerToast(`Loaded console: ${subTab.label}`, "info");
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg font-mono text-[9px] uppercase font-bold tracking-wider transition-all duration-200 cursor-pointer ${
                    isSubActive
                      ? "border-[#e840f3] bg-[#e840f3]/15 text-white shadow-[0_0_12px_rgba(232,64,243,0.15)]"
                      : "border-white/5 bg-black/40 text-slate-400 hover:border-purple-500/25 hover:text-white"
                  }`}
                >
                  <Icon size={11} className={subTab.color} />
                  <span>{subTab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {activeSubTab === 'CAMPAIGN_AND_TELEMETRY' ? (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        
        {/* LEFT COLUMN: ACTIVE ECOSYSTEM NODES & REAL-TIME CPC GAUGE (1/3 Width) */}
        <div className="space-y-6">
          
          {/* MONITOR: HIGH-LEVEL AD ECOSYSTEM NODE MATRIX */}
          <div className="bg-black/70 rounded-3xl border border-purple-500/20 p-5 space-y-4 shadow-xl backdrop-blur-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 py-2 px-3 bg-purple-500/10 text-[#e840f3] font-mono text-[8px] uppercase tracking-widest font-black rounded-bl-xl border-l border-b border-purple-500/15">
              SECURE GRID
            </div>

            <div className="flex items-center gap-2 pb-2 border-b border-white/5">
              <Zap size={15} className="text-[#00e5ff]" />
              <h2 className="text-xs font-mono font-bold text-white uppercase tracking-wider">
                Ad Ecosystem Active Node Matrix
              </h2>
            </div>
            <p className="text-[11px] text-zinc-400 leading-normal font-sans">
              Real-time monitoring of specialized system node grids executing AI Ad campaigns and automated Secure Payment Provider routing actions:
            </p>

            {/* Active Nodes Grid Layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-2.5">
              {ecosystemNodes.map((node) => {
                const isActive = activeAdNode === node.id;
                return (
                  <button
                    key={node.id}
                    id={`btn-node-${node.id.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => {
                      setActiveAdNode(node.id);
                      addLog(`OPERATIONS: Shifted primary command telemetry to ${node.name.toUpperCase()} (${node.id}).`);
                      triggerToast(`Switched telemetry focal node to ${node.id}`, "info");
                    }}
                    className={`p-3 rounded-2xl border text-left transition-all duration-300 relative overflow-hidden cursor-pointer group ${
                      isActive 
                        ? "bg-purple-950/20 border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.1)]" 
                        : "bg-[#0b0318]/40 border-white/5 hover:border-purple-500/35 hover:bg-black/60"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-[7.5px] font-mono font-bold uppercase text-zinc-500">
                        {node.id} • {node.role}
                      </span>
                      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-cyan-400 animate-pulse" : "bg-emerald-500"}`} />
                    </div>

                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-[11px] font-extrabold text-white block group-hover:text-cyan-400 transition-colors">
                          {node.name}
                        </span>
                        <span className="text-[9px] font-mono text-zinc-500 block uppercase">
                          {node.status}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] font-mono text-emerald-400 block font-bold leading-none">
                          {node.performance}% CTR
                        </span>
                        <span className="text-[8px] font-mono text-zinc-500 block">
                          SLA: {node.loading}s
                        </span>
                      </div>
                    </div>

                    {/* Progress tracking bar */}
                    <div className="w-full bg-zinc-950 h-1 mt-2.5 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${isActive ? "bg-gradient-to-r from-[#00e5ff] to-[#e840f3]" : "bg-zinc-800"}`}
                        style={{ width: `${node.performance}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* REAL-TIME METRIC DIAL GUAGE */}
          <div className="bg-black/70 rounded-3xl border border-white/5 p-5 space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <span className="text-[9px] font-mono text-yellow-400 uppercase tracking-widest block font-bold">
                ✓ Radial Telemetry Swivel
              </span>
              <span className="text-[8.5px] font-mono text-zinc-500">Node-12.7 Audit</span>
            </div>

            <div className="flex justify-around items-center gap-2">
              <div className="text-center space-y-1">
                <span className="text-zinc-500 uppercase block font-mono text-[8px] tracking-wider">Estimated CPM</span>
                <span className="text-lg font-mono font-black text-rose-400">£12.45</span>
                <span className="text-[8px] font-mono bg-rose-950/40 text-rose-300 px-1.5 py-0.5 rounded border border-rose-900/10 block uppercase font-bold">-4.2% Lower CPC</span>
              </div>
              
              <div className="border-l border-white/5 h-12" />

              <div className="text-center space-y-1">
                <span className="text-zinc-500 uppercase block font-mono text-[8px] tracking-wider">AIOps Lead Cost</span>
                <span className="text-lg font-mono font-black text-cyan-400">£0.38</span>
                <span className="text-[8px] font-mono bg-cyan-950/40 text-cyan-300 px-1.5 py-0.5 rounded border border-cyan-900/10 block uppercase font-bold">8.6x High Return</span>
              </div>

              <div className="border-l border-white/5 h-12" />

              <div className="text-center space-y-1">
                <span className="text-zinc-500 uppercase block font-mono text-[8px] tracking-wider">resource Sync</span>
                <span className="text-lg font-mono font-black text-emerald-400">99.8%</span>
                <span className="text-[8px] font-mono bg-emerald-950/40 text-emerald-300 px-1.5 py-0.5 rounded border border-emerald-900/10 block uppercase font-bold">Secure Guard</span>
              </div>
            </div>

            <div className="bg-[#030602] border border-emerald-500/10 p-3 rounded-2xl text-[9px] font-mono leading-relaxed text-zinc-400">
              <div className="flex items-center gap-1.5 text-emerald-400 font-bold mb-1 uppercase tracking-wide text-[9.5px]">
                <Activity size={10} className="animate-pulse" />
                <span>Ledger Synchronized Sweep Active</span>
              </div>
              Accounting link: All processing fees routed straight via <strong className="text-white">Secure Payment Provider sort-code 04-**-**</strong>. Backup mirroring tracks to Google Drive NexoraX Resource Library.
            </div>
          </div>

        </div>

        {/* MIDDLE COLUMN: ADVANCED AI AD COMPILER (MIRRORED FACTORY FUNCTIONALITY) (2/3 Width total) */}
        <div className="xl:col-span-2 space-y-6">

          {/* PRESENTS PRESETS SECTOR */}
          <div className="bg-black/70 border border-purple-500/20 p-5 rounded-3xl space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <span className="text-[9.5px] font-mono text-cyan-400 uppercase tracking-widest block font-bold">
                ⚡ Premium Campaign Presets (Live Overload Interface)
              </span>
              <span className="text-[8px] font-mono text-zinc-500">SC887095</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                {
                  name: "Elite Talent Acquisition",
                  prod: "NexoraX Principal Private Secretary Selection",
                  offer: "Zero placement fee scheme. Fully billed to NexoraX billing records (NI NH******B) under SC887095.",
                  audience: "NexoraX Executive Agencies & Venture Recruiters",
                  goal: "Rapid candidate interview scheduling",
                  cta: "Call hotline 07359****** or apply direct"
                },
                {
                  name: "Resource £150,000 resource Push",
                  prod: "NexoraX Resource NexoraX Infrastructure",
                  offer: "Secure bare-metal licensing aligning with Resource parameters and Secure Payment Provider Sort 04-**-** Account ******15.",
                  audience: "Enterprise Technology Officers & VCs",
                  goal: "Direct infrastructure licensing locks",
                  cta: "Claim direct access to Resource corporate vaults"
                },
                {
                  name: "0.8s Autopilot Shunt",
                  prod: "NexoraX Autonomous Node Engine",
                  offer: "Zero-latency shunting grid featuring defensive duplicate shield logs.",
                  audience: "Systems Architects & Directors",
                  goal: "Grid node enrollment verification",
                  cta: "Log to Chronos telemetry system"
                }
              ].map((tpl) => (
                <button
                  key={tpl.name}
                  id={`preset-${tpl.name.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => {
                    setSelectedPreset(tpl.name);
                    setProductName(tpl.prod);
                    setProductOffer(tpl.offer);
                    setTargetAudience(tpl.audience);
                    setCampaignGoal(tpl.goal);
                    setCampaignCta(tpl.cta);
                    triggerToast(`Loaded preset parameters: ${tpl.name}`, "success");
                    addLog(`TEMPLATES: Reset Advanced Console layout to "${tpl.name.toUpperCase()}" template.`);
                  }}
                  className={`p-3 rounded-2xl border text-left transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] cursor-pointer ${
                    selectedPreset === tpl.name
                      ? "border-[#e840f3] bg-[#e840f3]/10 text-white"
                      : "border-white/5 bg-[#090414]/80 text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  <span className="text-[#00e5ff] font-bold text-[10px] block mb-1 uppercase tracking-wide">
                    ✦ {tpl.name}
                  </span>
                  <span className="text-[9.5px] line-clamp-1 block text-zinc-300 font-mono">
                    Prod: {tpl.prod}
                  </span>
                  <span className="text-[8px] line-clamp-2 block text-zinc-500 font-mono mt-1 leading-normal">
                    {tpl.offer}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* ADVANCED CAMPAIGN BUILDER FORM */}
          <div className="bg-[#0b0318]/95 rounded-3xl border border-purple-500/20 p-6 space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-2 pb-2.5 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse" />
                <h3 className="text-sm font-mono font-bold text-white uppercase tracking-wider">
                  Mirrored Ad Studio Synthesis Suite
                </h3>
              </div>
              <span className="text-[8px] font-mono bg-purple-900/30 px-2 py-0.5 rounded text-purple-300 border border-purple-500/25">DYNAMIC HUBS</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
              <div className="space-y-1.5 text-left">
                <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Product / Service Name</label>
                <input 
                  type="text"
                  id="inp-product-name"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  className="w-full bg-black/70 border border-white/10 p-2.5 rounded-xl text-white outline-none focus:border-cyan-500 transition-colors"
                />
              </div>

              <div className="space-y-1.5 text-left">
                <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Target Audience Context</label>
                <input 
                  type="text"
                  id="inp-target-audience"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  className="w-full bg-black/70 border border-white/10 p-2.5 rounded-xl text-white outline-none focus:border-purple-500 transition-colors"
                />
              </div>

              <div className="space-y-1.5 text-left md:col-span-2">
                <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Product Offer & Subsidies Details</label>
                <textarea 
                  id="inp-product-offer"
                  value={productOffer}
                  onChange={(e) => setProductOffer(e.target.value)}
                  rows={2}
                  className="w-full bg-black/70 border border-white/10 p-2.5 rounded-xl text-white outline-none focus:border-[#e840f3] transition-colors leading-relaxed"
                />
              </div>

              <div className="space-y-1.5 text-left">
                <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Campaign Goal</label>
                <input 
                  type="text"
                  id="inp-campaign-goal"
                  value={campaignGoal}
                  onChange={(e) => setCampaignGoal(e.target.value)}
                  className="w-full bg-black/70 border border-white/10 p-2.5 rounded-xl text-white outline-none focus:border-cyan-500 transition-colors"
                />
              </div>

              <div className="space-y-1.5 text-left">
                <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Call To Action directive</label>
                <input 
                  type="text"
                  id="inp-campaign-cta"
                  value={campaignCta}
                  onChange={(e) => setCampaignCta(e.target.value)}
                  className="w-full bg-black/70 border border-white/10 p-2.5 rounded-xl text-white outline-none focus:border-[#e840f3] transition-colors"
                />
              </div>

              <div className="space-y-1.5 text-left">
                <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Select Active Ad Platform</label>
                <select
                  id="select-platform-id"
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                  className="w-full bg-black/80 border border-white/10 p-2.5 rounded-xl text-zinc-300 outline-none focus:border-cyan-500"
                >
                  <option>LinkedIn</option>
                  <option>YouTube Cinematic Preroll</option>
                  <option>TikTok Vertical</option>
                  <option>Meta Networks</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  id="btn-synthesize-campaign"
                  type="button"
                  onClick={handleSynthesizeAdvancedAd}
                  disabled={generationLoading}
                  className="w-full py-2.5 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 text-black uppercase font-bold tracking-widest text-[10px] rounded-xl hover:opacity-90 active:scale-[0.98] transition-all cursor-pointer shadow-lg font-mono"
                >
                  {generationLoading ? "CALCULATING NODE GEOMETRY..." : "⚡ Synthesize Mirrored Campaign"}
                </button>
              </div>
            </div>

            {/* Generated Campaign Output Case (Minimised sandbox rendering) */}
            <AnimatePresence mode="wait">
              {generatedAdvCampaign ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="p-5 rounded-2xl bg-[#04010a] border border-cyan-500/30 space-y-4"
                >
                  <div className="flex justify-between items-center pb-2 border-b border-white/5 uppercase font-mono text-[9px]">
                    <span className="text-cyan-400 font-bold flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                      SYNTHESIZED HARVEST AD PERMUTATIONS
                    </span>
                    <span className="text-zinc-500">Node Synchronized</span>
                  </div>

                  <div className="space-y-2.5 text-zinc-300 font-sans text-xs">
                    <div>
                      <span className="text-purple-400 font-mono text-[8px] block uppercase font-bold">NEXORAX HEADLINE COPY</span>
                      <p className="text-white font-mono font-black text-[12px]">{generatedAdvCampaign.header}</p>
                    </div>
                    <div className="border-t border-white/5 pt-2">
                      <span className="text-purple-400 font-mono text-[8px] block uppercase font-bold">AD BODY COPY</span>
                      <p className="leading-relaxed text-[11px] font-sans">{generatedAdvCampaign.body}</p>
                    </div>

                    <div className="flex flex-wrap gap-1 pt-1.5">
                      {generatedAdvCampaign.tags.map((tag: string, i: number) => (
                        <span key={i} className="text-[8.5px] font-mono bg-purple-950/40 text-purple-300 border border-purple-500/10 p-1 px-2 rounded-lg">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* PREDICTIVE REPORT GRID */}
                  <div className="grid grid-cols-3 gap-2.5 pt-3 border-t border-white/5 font-mono text-center">
                    <div className="p-2 bg-[#02050b] rounded-xl border border-white/5">
                      <span className="text-zinc-500 block uppercase text-[7.5px]">CTR EST</span>
                      <span className="text-[#00e5ff] font-bold text-xs">{generatedAdvCampaign.ctrEstimate}</span>
                    </div>
                    <div className="p-2 bg-[#02050b] rounded-xl border border-white/5">
                      <span className="text-zinc-500 block uppercase text-[7.5px]">MONTHLY LEADS</span>
                      <span className="text-emerald-400 font-bold text-xs">{generatedAdvCampaign.monthlyLeads}</span>
                    </div>
                    <div className="p-2 bg-[#02050b] rounded-xl border border-white/5">
                      <span className="text-zinc-500 block uppercase text-[7.5px]">NEXORAX METRIC</span>
                      <span className="text-yellow-400 font-bold text-xs">0.8s SLA</span>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <div className="p-6 rounded-2xl border border-dashed border-white/5 bg-black/30 text-center font-mono text-xs text-zinc-500">
                  ⚡ Ready to synthesize live variables. Select preset or enter information above.
                </div>
              )}
            </AnimatePresence>
          </div>

        </div>

      </div>

      {/* PERFORMANCE FEED: ECOSYSTEM MONITOR AND AD FACTORY REAL-TIME KPIS */}
      <div className="bg-black/70 rounded-3xl border border-purple-500/20 p-6 space-y-6 shadow-xl backdrop-blur-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 py-2 px-3 bg-purple-500/10 text-[#e840f3] font-mono text-[8px] uppercase tracking-widest font-black rounded-bl-xl border-l border-b border-purple-500/15 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-[#e840f3] animate-pulse" />
          ACTIVE MONITOR POLLING
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b border-white/5">
          <div className="space-y-1.5 text-left">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase font-bold tracking-widest text-cyan-400 font-mono">✦ NODE-07.8 PERFORMANCE INTEGRATION</span>
              <span className="text-[8px] bg-cyan-950/60 text-[#00e5ff] px-2 rounded-md font-sans border border-cyan-500/15 uppercase font-black tracking-widest">LIVE KPI MONITOR</span>
            </div>
            <h2 className="text-xl font-sans font-black uppercase text-white tracking-tight">
              Ad Factory Real-Time KPI & Generation Feed
            </h2>
            <p className="text-xs text-zinc-400 max-w-3xl leading-relaxed">
              Continuous telemetry stream pulling high-octane performance diagnostics, content ratios, and conversion parameters of active AI campaigns and visual assets. Check stats sync recursively at 0.8s resolution.
            </p>
          </div>

          <div className="flex flex-wrap gap-2.5">
            <button
              id="btn-calibrate-metrics"
              type="button"
              onClick={handleResetFactoryMetrics}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-950/20 border border-rose-500/25 text-rose-300 font-mono text-[9px] uppercase font-black rounded-xl hover:bg-rose-900/40 transition-all cursor-pointer"
            >
              <Sliders size={10} />
              <span>Reset/Calibrate Telemetry</span>
            </button>
            <button
              id="btn-sync-diagnostics"
              type="button"
              onClick={handleForceSync}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-950/20 border border-cyan-500/20 text-[#00e5ff] font-mono text-[9px] uppercase font-black rounded-xl hover:bg-cyan-900/40 transition-all cursor-pointer"
            >
              <RefreshCw size={10} className="animate-spin-slow" />
              <span>Sync Diagnostic Grid</span>
            </button>
          </div>
        </div>

        {/* METRICS GRID */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            {
              title: "Campaign Matrices",
              val: totalCampaignsGen,
              sub: "SLA Calibrated",
              accent: "text-cyan-400 border-cyan-500/10 hover:border-cyan-500/30",
              dot: "bg-cyan-400"
            },
            {
              title: "UGC Video Renders",
              val: totalUgcVideos,
              sub: "Eleanor/Candidate models",
              accent: "text-purple-400 border-purple-500/10 hover:border-purple-500/30",
              dot: "bg-purple-400"
            },
            {
              title: "Creative Graphics",
              val: totalCreatives,
              sub: "High-vivid styling",
              accent: "text-pink-400 border-pink-500/10 hover:border-pink-500/30",
              dot: "bg-pink-400"
            },
            {
              title: "Hooks Engineered",
              val: totalHooks,
              sub: "NexoraX messaging",
              accent: "text-blue-400 border-blue-500/10 hover:border-blue-500/30",
              dot: "bg-blue-400"
            },
            {
              title: "A/B Testing Pools",
              val: totalAbVariations,
              sub: "Monte Carlo tracks",
              accent: "text-yellow-400 border-yellow-500/10 hover:border-yellow-500/30",
              dot: "bg-yellow-400"
            },
            {
              title: "Campaigns Dispatched",
              val: totalPublished,
              sub: `Platform: ${lastPublishedChan}`,
              accent: "text-emerald-400 border-emerald-500/10 hover:border-emerald-500/30",
              dot: "bg-emerald-400"
            }
          ].map((m, idx) => (
            <div
              key={idx}
              className={`bg-zinc-950/60 p-4 rounded-2xl border text-left transition-all duration-300 relative group overflow-hidden ${m.accent}`}
            >
              <div className="absolute top-0 right-0 w-8 h-8 bg-white/5 rounded-full -mr-2 -mt-2 group-hover:scale-150 transition-all duration-500 opacity-20" />
              <div className="flex justify-between items-center mb-1">
                <span className="text-[8.5px] font-mono text-zinc-500 uppercase tracking-wider block font-bold truncate pr-2">
                  {m.title}
                </span>
                <span className={`w-1.5 h-1.5 rounded-full ${m.dot} animate-pulse`} />
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-mono font-black text-white">
                  {m.val}
                </span>
                <span className="text-[7.5px] font-mono text-zinc-500 uppercase block">Units</span>
              </div>
              <p className="text-[9px] text-zinc-400 truncate mt-1">
                {m.sub}
              </p>
            </div>
          ))}
        </div>

        {/* CHARTS ROW & LIVE TELEMETRY TICKER */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Generation Intensity Chart (2/3 width) */}
          <div className="lg:col-span-2 bg-zinc-950/50 rounded-2xl border border-white/5 p-4 space-y-3">
            <div className="flex justify-between items-center pb-1">
              <h3 className="text-xs font-mono font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                <Activity size={12} className="text-cyan-400 animate-pulse" />
                NexoraX Content Generation & CTR Calibration Timeline
              </h3>
              <span className="text-[9px] font-mono bg-cyan-950/50 text-[#22d3ee] p-1 px-2.5 rounded border border-cyan-500/20 font-bold">
                Ecosystem Average CTR: {factoryAvgCtr}%
              </span>
            </div>

            <div className="h-44 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={[
                    { name: "Hooks", units: totalHooks, ctr: factoryAvgCtr * 1.05 },
                    { name: "A/B Pools", units: totalAbVariations, ctr: factoryAvgCtr * 1.2 },
                    { name: "UGC Videos", units: totalUgcVideos, ctr: factoryAvgCtr * 1.45 },
                    { name: "Creatives", units: totalCreatives, ctr: factoryAvgCtr * 0.95 },
                    { name: "Campaigns", units: totalCampaignsGen, ctr: factoryAvgCtr * 1.1 },
                    { name: "Disseminated", units: totalPublished, ctr: factoryAvgCtr * 1.35 }
                  ]}
                  margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorUnits" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#e840f3" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#e840f3" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorCTR" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.3)" fontSize={8.5} />
                  <YAxis stroke="rgba(255,255,255,0.3)" fontSize={8.5} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#090412", borderColor: "rgba(147, 51, 234, 0.4)", borderRadius: "10px" }}
                    itemStyle={{ color: "#fff", fontSize: "10px", fontFamily: "monospace" }}
                    labelStyle={{ color: "#22d3ee", fontSize: "10px", fontFamily: "monospace", fontWeight: "bold" }}
                  />
                  <Area type="monotone" dataKey="units" stroke="#e840f3" strokeWidth={1.5} fillOpacity={1} fill="url(#colorUnits)" name="Asset Units" />
                  <Area type="monotone" dataKey="ctr" stroke="#22d3ee" strokeWidth={2} fillOpacity={1} fill="url(#colorCTR)" name="CTR Calibration %" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Real-time Ticker Feed (1/3 width) */}
          <div className="bg-zinc-950/50 rounded-2xl border border-white/5 p-4 flex flex-col justify-between">
            <div className="space-y-2">
              <div className="flex justify-between items-center pb-2 border-b border-white/5 font-mono text-[10px]">
                <span className="text-zinc-400 uppercase tracking-widest font-bold flex items-center gap-1 text-left">
                  <Terminal size={11} className="text-cyan-400 animate-pulse" />
                  Real-time Signal Ticker
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
              </div>

              <div className="h-32 overflow-y-auto font-mono text-[9px] text-zinc-400 space-y-1.5 text-left custom-scrollbar pr-1 leading-relaxed">
                <div className="border-b border-white/5 pb-1 last:border-0">
                  <span className="text-cyan-400 font-bold">[OK]</span> <span className="text-zinc-500">{(new Date()).toLocaleTimeString()}</span> <strong className="text-white">ECOSYSTEM:</strong> Active CTR calibrated dynamically at <span className="text-[#00e5ff] font-bold">{factoryAvgCtr}%</span>
                </div>
                <div className="border-b border-white/5 pb-1 last:border-0">
                  <span className="text-cyan-400 font-bold">[OK]</span> <span className="text-zinc-500">{(new Date()).toLocaleTimeString()}</span> <strong className="text-white">TELEMETRY:</strong> Total asset counters loaded successfully. Active workflow tracks synced.
                </div>
                {totalPublished > 8 ? (
                  <div className="border-b border-white/5 pb-1 last:border-0">
                    <span className="text-emerald-400 font-bold">[DISPATCH]</span> <span className="text-zinc-500">{(new Date()).toLocaleTimeString()}</span> <strong className="text-emerald-300">MULTICHANNEL:</strong> Deployed fresh ad pack directly to <span className="text-white font-bold">{lastPublishedChan}</span>.
                  </div>
                ) : null}
                {totalUgcVideos > 24 ? (
                  <div className="border-b border-white/5 pb-1 last:border-0">
                    <span className="text-purple-400 font-bold">[RENDER]</span> <span className="text-zinc-500">{(new Date()).toLocaleTimeString()}</span> <strong className="text-purple-300">UGC_VOICE:</strong> Synthesized audio-visual biometric anchor for Eleanor v2.
                  </div>
                ) : null}
                <div className="border-b border-white/5 pb-1 last:border-0">
                  <span className="text-emerald-400 font-bold">[AUDIT_SEC]</span> <span className="text-zinc-500">{(new Date()).toLocaleTimeString()}</span> <strong className="text-zinc-200">FINANCIAL:</strong> 3% Community Allocation & tax ratios verified. Secure Payment Provider link established.
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-white/5 flex justify-between items-center text-[8.5px] font-mono text-zinc-500 uppercase">
              <span>NexoraX ID: SC887095</span>
              <span className="text-yellow-400 font-bold">GRID SYNC SECURE</span>
            </div>
          </div>
        </div>
      </div>

      {/* DYNAMIC INTEGRATION ROW: LIVE PERFORMANCE FEEDING FROM AD STUDIO (2 columns style) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* DOUBLE COLUMN: LIVE LEARNING METRICS COCKPIT */}
        <div className="lg:col-span-2 bg-[#0b0318]/95 rounded-3xl border-2 border-emerald-500/20 p-6 space-y-6 relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 py-2.5 px-4 bg-emerald-500/10 text-emerald-400 font-mono text-[8.5px] uppercase font-black tracking-widest border-l border-b border-emerald-500/25 rounded-bl-xl flex items-center gap-1.5 animate-pulse">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
            Live Feeding Active
          </div>

          <div className="flex justify-between items-center pb-3 border-b border-white/5">
            <h3 className="text-sm font-mono font-black text-white uppercase tracking-wider flex items-center gap-2">
              <span>9. Primary Performance Learning Linkage cockpit</span>
            </h3>
            
            <button
              id="btn-force-sync"
              type="button"
              onClick={handleForceSync}
              className="flex items-center gap-1 px-3 py-1 bg-emerald-950/60 border border-emerald-500/30 text-emerald-300 font-mono text-[9px] uppercase font-black rounded-lg hover:bg-emerald-900/40 transition-colors cursor-pointer"
            >
              <RefreshCw size={10} className="animate-spin-slow" />
              <span>Sync Ad Factory Now</span>
            </button>
          </div>

          <p className="text-xs text-zinc-400 leading-normal font-sans">
            This module dynamically references real-time performance learning variables updated directly by the AI Ad Factory or UGC Generator. This data calibrates the Secure Payment Provider subsidy levels and SecurePayment Ledger pathways recursively:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-mono">
            {/* CARD 1: CONVERSION CHAMPIONS */}
            <div className="bg-[#030805] p-4 rounded-2xl border-2 border-emerald-500/20 space-y-2.5 relative">
              <span className="text-emerald-400 text-[10px] uppercase font-black block tracking-wider">
                ★ LIVE CONVERSION CHAMPIONS
              </span>

              <div className="space-y-2 text-[11px] text-zinc-300 font-sans">
                <div className="p-2 bg-black/40 rounded border border-white/5">
                  <strong className="font-mono text-[8px] text-zinc-500 block uppercase">Best Performing Hook:</strong>
                  <span className="text-white font-mono tracking-wide">{bestHook}</span>
                </div>
                
                <div className="p-2 bg-black/40 rounded border border-white/5">
                  <strong className="font-mono text-[8px] text-zinc-500 block uppercase">Best Lead Avatar:</strong>
                  <span className="text-white font-mono tracking-wide">{bestAvatar}</span>
                </div>

                <div className="p-2 bg-black/40 rounded border border-white/5">
                  <strong className="font-mono text-[8px] text-zinc-500 block uppercase">Dominant Ad Destination:</strong>
                  <span className="text-white font-mono tracking-wide">{bestDestination}</span>
                </div>

                <div className="p-2 bg-black/40 rounded border border-white/5">
                  <strong className="font-mono text-[8px] text-zinc-500 block uppercase">Best Video Length:</strong>
                  <span className="text-emerald-400 font-bold">{bestVideoLength}</span>
                </div>
              </div>
            </div>

            {/* CARD 2: DEMOGRAPHIC CHAMPIONS */}
            <div className="bg-[#030805] p-4 rounded-2xl border-2 border-[#00e5ff]/20 space-y-2.5 relative">
              <span className="text-[#00e5ff] text-[10px] uppercase font-black block tracking-wider">
                ★ LIVE DEMOGRAPHIC CHAMPIONS
              </span>

              <div className="space-y-2 text-[11px] text-zinc-300 font-sans">
                <div className="p-2 bg-black/40 rounded border border-white/5">
                  <strong className="font-mono text-[8px] text-zinc-500 block uppercase">Best Call to Action:</strong>
                  <span className="text-white font-mono tracking-wide">{bestCta}</span>
                </div>

                <div className="p-2 bg-black/40 rounded border border-white/5">
                  <strong className="font-mono text-[8px] text-zinc-500 block uppercase">Winning Language:</strong>
                  <span className="text-white font-mono tracking-wide">{bestLanguage}</span>
                </div>

                <div className="p-2 bg-black/40 rounded border border-white/5">
                  <strong className="font-mono text-[8px] text-zinc-500 block uppercase">Target Audience Cohort:</strong>
                  <span className="text-white font-mono tracking-wide">{bestAudience}</span>
                </div>

                <div className="p-2 bg-black/40 rounded border border-white/5">
                  <strong className="font-mono text-[8px] text-zinc-500 block uppercase">Woven Visual Accent:</strong>
                  <span className="text-cyan-400 font-semibold">{bestVisual}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SINGLE COLUMN: INTERACTIVE TELEMETRY LOGS */}
        <div className="bg-black/70 border border-white/10 p-5 rounded-3xl space-y-4">
          <div className="flex justify-between items-center pb-2 border-b border-white/5 font-mono text-xs">
            <span className="text-zinc-400 uppercase tracking-widest font-black flex items-center gap-1">
              <Terminal size={12} className="text-cyan-400" />
              Telemetry Log Monitor
            </span>
            <button
              id="btn-clear-advanced-logs"
              onClick={() => {
                setLogs([`[${new Date().toLocaleTimeString()}] Telemetry log block cleared successfully.`]);
                triggerToast("Logs cleared!", "info");
              }}
              className="text-[8px] text-zinc-500 hover:text-zinc-300 uppercase underline cursor-pointer"
            >
              Clear
            </button>
          </div>

          <div className="h-56 overflow-y-auto bg-black/90 p-3 rounded-xl border border-white/5 font-mono text-[9.5px] text-zinc-400 space-y-1.5 custom-scrollbar text-left leading-relaxed">
            {logs.map((log, i) => (
              <div key={i} className="border-b border-white/5 pb-1 last:border-0 hover:text-white transition-colors">
                {log}
              </div>
            ))}
          </div>

          <div className="bg-purple-950/20 border border-purple-500/10 p-2.5 rounded-2xl flex items-center justify-between text-[10px] font-mono gap-2 text-left">
            <div>
              <span className="text-purple-400 font-bold uppercase block text-[8px]">PRIMARY OPERATIONAL LINK</span>
              <span className="text-slate-300 uppercase tracking-tight block">Toll-Free Primary Phone Dial</span>
            </div>
            <a 
              href="tel:08000 622827"
              id="link-voice-anchor"
              className="px-2.5 py-1 bg-purple-600 text-white font-bold uppercase rounded hover:bg-purple-500 transition-colors cursor-pointer"
            >
              08000 622827
            </a>
          </div>
        </div>

      </div>
        </>
      ) : (
        <ConsoleErrorBoundary key={activeSubTab} fallbackModuleName={activeSubTab.replace(/_/g, " ")}>
          {activeSubTab === "AI_AGENT_AUTOMATION" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <NexusAgents
                user={user}
                onAddLog={handleAddLog}
                onDeductTasks={handleDeductTasks}
              />
            </motion.div>
          ) : activeSubTab === "GALAXY_LAB" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <NexusGalaxyLab
                user={user}
                isAdminMode={true}
                onAddLog={handleAddLog}
                onAddTaskToWorkSuite={(task) => {
                  const newTask: WorkSuiteTask = {
                    id: `task-${Date.now()}`,
                    title: task.title || "Untitled Galaxy Task",
                    project: task.project || "Galaxy Lab",
                    client: task.client || "Director Office",
                    priority: task.priority || "HIGH",
                    status: "TODO"
                  };
                  setWorkSuiteTasks(prev => [newTask, ...prev]);
                  addLog(`GALAXY: Added task "${newTask.title}" to standard workspace.`);
                }}
                searchCredits={user.searchCredits}
                onAddSearchCredits={(price, amount) => {
                  setUser(prev => ({ ...prev, searchCredits: prev.searchCredits + amount }));
                  addLog(`GALAXY: Added ${amount} search credits for £${price}.`);
                }}
                currentRole="god-architect"
              />
            </motion.div>
          ) : activeSubTab === "MAIL_DOMAINS" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <NexusMailDomains
                user={user}
                isAdminMode={true}
                onAddLog={handleAddLog}
                onAddTaskToWorkSuite={(title, priority) => {
                  const newTask: WorkSuiteTask = {
                    id: `task-${Date.now()}`,
                    title,
                    project: "Mail & Domains Platform",
                    client: "Domain Admin",
                    priority: priority || "HIGH",
                    status: "TODO"
                  };
                  setWorkSuiteTasks(prev => [newTask, ...prev]);
                  addLog(`MAIL_DOMAINS: Added task "${title}" to standard workspace.`);
                }}
                searchCredits={user.searchCredits}
                onAddSearchCredits={(amount, cost) => {
                  setUser(prev => ({ ...prev, searchCredits: prev.searchCredits + amount }));
                  addLog(`MAIL_DOMAINS: Added ${amount} search credits for £${cost}.`);
                }}
              />
            </motion.div>
          ) : activeSubTab === "NEXUS_SEARCH" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <NexusSearch
                user={user}
                onAddLog={handleAddLog}
                onDeductCredits={(amt) => {
                  setUser(prev => ({ ...prev, searchCredits: Math.max(0, prev.searchCredits - amt) }));
                  addLog(`SEARCH: Deducted ${amt} search credits.`);
                }}
                isAdminMode={true}
              />
            </motion.div>
          ) : activeSubTab === "NEXUS_HOSTING" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <NexusHosting
                user={user}
                isAdminMode={true}
                onDeploy={() => {
                  addLog(`HOSTING: Deployed live node server instance.`);
                }}
                onAddLog={handleAddLog}
              />
            </motion.div>
          ) : activeSubTab === "MARKETPLACE" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <NexusMarketplace
                user={user}
                isAdmin={true}
                onPurchaseCredits={(amt, cost) => {
                  setUser(prev => ({ ...prev, searchCredits: prev.searchCredits + amt }));
                  addLog(`MARKETPLACE: Purchased ${amt} search credits for £${cost}.`);
                }}
                onUpgradePlan={(newPlan) => {
                  setUser(prev => ({ ...prev, plan: newPlan as any }));
                  addLog(`MARKETPLACE: Upgraded plan to ${newPlan}.`);
                }}
                onAddLog={handleAddLog}
              />
            </motion.div>
          ) : activeSubTab === "WORK_SUITE" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <NexusWorkSuite
                user={user}
                onAddLog={handleAddLog}
                tasks={workSuiteTasks}
                onTasksChanged={(updated) => {
                  setWorkSuiteTasks(updated);
                }}
              />
            </motion.div>
          ) : activeSubTab === "NEXUS_CHAT" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <NexusChat
                user={user}
                onAddLog={handleAddLog}
                onAddTaskFromChat={(text) => {
                  const newTask: WorkSuiteTask = {
                    id: `task-${Date.now()}`,
                    title: `CHAT MEMO: "${text.substring(0, 45).toUpperCase()}..."`,
                    project: "Social Conversion",
                    client: "Lobby Visitor",
                    priority: "HIGH",
                    status: "TODO"
                  };
                  setWorkSuiteTasks(prev => [newTask, ...prev]);
                  addLog(`CHAT: Added dynamic task from workspace chat log.`);
                }}
                onSearchFromChat={(query) => {
                  setActiveSubTab("NEXUS_SEARCH");
                }}
                onDeployAgent={(role) => {
                  setActiveSubTab("AI_AGENT_AUTOMATION");
                }}
              />
            </motion.div>
          ) : activeSubTab === "MEDIA_AD_SOCIAL" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <NexusMediaAdSocial
                user={user}
                onAddLog={handleAddLog}
                onDeductRenders={(amt) => {
                  setUser(prev => ({ ...prev, rendersRemaining: Math.max(0, prev.rendersRemaining - amt) }));
                }}
                posts={socialPosts}
                onPostsChanged={(updated) => {
                  setSocialPosts(updated);
                }}
              />
            </motion.div>
          ) : activeSubTab === "ORGANIC_GROWTH" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full">
              <NexusOrganicGrowthEngine
                user={user}
                isAdminMode={true}
                onAddLog={handleAddLog}
                onAddTaskToWorkSuite={(title, priority) => {
                  const newTask: WorkSuiteTask = {
                    id: `task-${Date.now()}`,
                    title,
                    project: "Organic Growth",
                    client: "Organic Growth Engine",
                    priority: priority || "HIGH",
                    status: "TODO"
                  };
                  setWorkSuiteTasks(prev => [newTask, ...prev]);
                  addLog(`ORGANIC_GROWTH: Added task "${title}" to standard workspace.`);
                }}
                searchCredits={user.searchCredits}
                onAddSearchCredits={(amt, cost) => {
                  setUser(prev => ({ ...prev, searchCredits: prev.searchCredits + amt }));
                  addLog(`ORGANIC_GROWTH: Purchased ${amt} search credits.`);
                }}
                currentRole="admin"
              />
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full grid grid-cols-1 xl:grid-cols-2 gap-6">
              
              {/* LEFT CARD: HMRC GOVTALK GATEWAY SANDBOX & PILOT EMULATOR */}
              <div className="bg-gradient-to-b from-[#111] to-black rounded-3xl border border-amber-500/20 p-6 space-y-5 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 py-2 px-3.5 bg-amber-500/10 text-amber-400 font-mono text-[8px] uppercase tracking-widest font-black rounded-bl-xl border-l border-b border-amber-500/15">
                  MTD INTERFACE NODE-07
                </div>

                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping" />
                    <h2 className="text-base font-sans font-black uppercase text-amber-400 tracking-wider">HMRC GovTalk Message Sandbox</h2>
                  </div>
                  <p className="text-[10px] text-zinc-400 font-mono leading-relaxed">
                    Interactive Make Tax Digital (MTD) v2.4 envelope customizer. Edit and dispatch XML payloads for corporate reference <span className="text-white font-bold">SC887095</span>.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-[9px] uppercase font-mono font-bold text-zinc-400 flex justify-between">
                    <span>Raw GovTalkMessage XML Payload Editor</span>
                    <span className="text-[#00ffd2]">v2.4 Core Schema Compliance</span>
                  </label>
                  <textarea
                    id="govtalk-xml-payload-editor"
                    className="w-full h-[280px] bg-zinc-950/90 border border-zinc-800 text-amber-500 rounded-xl p-3.5 font-mono text-[9px] leading-normal focus:outline-none focus:border-amber-500/40 resize-none selection:bg-amber-500/20 selection:text-amber-300"
                    value={xmlPayloadText}
                    onChange={(e) => setXmlPayloadText(e.target.value)}
                  />
                </div>

                {/* Sub-Actions Array */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <button
                    id="validate-xml-schema-btn"
                    onClick={() => {
                      if (!xmlPayloadText.includes("<GovTalkMessage") || !xmlPayloadText.includes("SC887095")) {
                        triggerToast("Validation Failed: Missing required GovTalk Envelope or SC887095!", "error");
                        addLog("GATEWAY: Failed XML validation scan - schema mismatch.");
                      } else {
                        triggerToast("GovTalk Envelope Schema Validated against HMRC v2.4 SDK!", "success");
                        addLog("GATEWAY: Envelope schema successfully validated. Zero structures missing.");
                      }
                    }}
                    className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 font-mono text-[9px] font-extrabold uppercase rounded-xl tracking-widest transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    🔍 Validate XML Schema
                  </button>

                  <button
                    id="transmit-hmrc-gateway-btn"
                    disabled={isTransmittingHMRC}
                    onClick={() => {
                      setIsTransmittingHMRC(true);
                      setTransmitStepIdx(0);
                      const steps = [
                        "ESTABLISHING SECURE HANDSHAKE WITH HMRC CLOUD ENGINE...",
                        "TRANSMITTING ENVELOPED XML INJECT (SC887095)...",
                        "VERIFYING GUARDIAN DIRECT ACCESS CERTIFICATE FOR Lead Director...",
                        "HM REVENUE SUCCESS STATUS RECEIVED! TRANSACTION SECURED."
                      ];
                      setTransmitSteps(steps);
                      
                      // Sequential step trigger at 200ms latency matching the 0.8s standard!
                      let currentStep = 0;
                      const interval = setInterval(() => {
                        currentStep++;
                        if (currentStep < steps.length) {
                          setTransmitStepIdx(currentStep);
                          addLog(`HMRC GATEWAY: ${steps[currentStep]}`);
                        } else {
                          clearInterval(interval);
                          setIsTransmittingHMRC(false);
                          setTransmitStepIdx(-1);
                          triggerToast("MTD Tax Payload Transmitted Successfully (0.8s SLA)!", "success");
                          addLog("HMRC GATEWAY: System transaction sealed. Ledger mirrored in Private NexoraX Resource Library.");
                          
                          // Dispatch log directly to backend endpoint to persist in cryptographic ledger audit trail!
                          fetch('/api/ledger/audit-trail', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              timestamp: new Date().toISOString(),
                              action_token: 'XML_COMPILE',
                              operator_signature: 'Office of the Lead Architect',
                              sha256_checksum: 'HMRC_MTD_SYNC_' + Math.random().toString(36).substring(3, 10).toUpperCase()
                            })
                          }).catch(err => console.error("Error logging to private ledger audit:", err));
                        }
                      }, 200);
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-amber-500/25 to-amber-600/25 hover:from-amber-500/35 hover:to-amber-600/35 border border-amber-500 text-amber-300 font-mono text-[9px] font-extrabold uppercase rounded-xl tracking-widest transition-all cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-40"
                  >
                    {isTransmittingHMRC ? (
                      <>
                        <RefreshCw size={11} className="animate-spin text-amber-300" />
                        <span>TRANSMITTING...</span>
                      </>
                    ) : (
                      <>
                        <Send size={11} className="text-amber-400" />
                        <span>DISPATCH TO HMRC GATEWAY</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Secure Transmit Feedback Panel */}
                {isTransmittingHMRC && transmitStepIdx >= 0 && (
                  <div className="p-3 bg-zinc-950 border border-amber-500/25 rounded-xl font-mono text-[8.5px] leading-relaxed text-amber-400 animate-pulse space-y-1">
                    <div className="font-extrabold uppercase tracking-widest text-[#00ffd2]">NexoraX MTD Gateway Connection Stream:</div>
                    <div>{transmitSteps[transmitStepIdx]}</div>
                  </div>
                )}

                {/* Exporter Downloader Hook */}
                <div className="mt-2 text-zinc-400 border-t border-zinc-900 pt-4">
                  <NexoraxHMRCXMLExporter />
                </div>
              </div>

              {/* RIGHT CARD: NexoraX billing records DIRECT BILLING SCHEME MANAGEMENT */}
              <div className="bg-gradient-to-b from-[#111] to-black rounded-3xl border border-pink-500/20 p-6 space-y-6 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 py-2 px-3.5 bg-pink-500/10 text-pink-400 font-mono text-[8px] uppercase tracking-widest font-black rounded-bl-xl border-l border-b border-pink-500/15">
                  Client Resource Workflow SCHEME
                </div>

                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-ping" />
                    <h2 className="text-base font-sans font-black uppercase text-pink-400 tracking-wider">NexoraX billing records Secure Invoice Register Allocation</h2>
                  </div>
                  <p className="text-[10px] text-zinc-400 font-mono leading-relaxed">
                    Financial direct-billing engine under Secure Payment Provider National Insurance Scheme <span className="text-white font-bold">NH448796B</span>. Anchored against £150,000 Resource resource stabilizing NexoraX AI Infrastructure.
                  </p>
                </div>

                {/* Candidate Selection */}
                <div className="space-y-2">
                  <label className="text-[9px] uppercase font-mono font-bold text-zinc-400">Target Candidate Selection</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['Eleanor Vance', 'Helen', 'Claire'].map((candidate) => {
                      const isActive = selectedSecRegister === candidate;
                      return (
                        <button
                          key={candidate}
                          onClick={() => {
                            setSelectedSecRegister(candidate as any);
                            addLog(`COMPLIANCE: Evaluated Direct Billing metrics specifically for Candidate '${candidate.toUpperCase()}'.`);
                            triggerToast(`Auditing candidate profile: ${candidate}`, "info");
                          }}
                          className={`p-2.5 rounded-xl border font-mono text-[9px] font-bold text-center transition-all cursor-pointer ${
                            isActive
                              ? "bg-pink-500/15 border-pink-500 text-white shadow-md shadow-pink-500/5"
                              : "bg-zinc-950 border-zinc-800 text-slate-400 hover:text-slate-100 hover:border-zinc-700"
                          }`}
                        >
                          <Users size={12} className="mx-auto mb-1 text-pink-400" />
                          <span>{candidate}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Sliders Area */}
                <div className="space-y-4 bg-zinc-950/40 p-4 border border-zinc-900 rounded-2xl">
                  {/* Slider 1: Secure Payment Provider Approved Subsidy % */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-[9px] font-mono">
                      <span className="text-zinc-400 uppercase font-bold">Secure Payment Provider Approved Subsidy Coverage</span>
                      <span className="text-pink-400 font-extrabold">{approvedSubsidyPct}%</span>
                    </div>
                    <input
                      type="range"
                      min="50"
                      max="100"
                      value={approvedSubsidyPct}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setApprovedSubsidyPct(val);
                        addLog(`COMPLIANCE: Calibrated NexoraX billing records subsidy coverage to ${val}%.`);
                      }}
                      className="w-full accent-pink-500 bg-zinc-900 rounded-lg h-1.5 cursor-pointer"
                    />
                    <div className="flex justify-between text-[8px] text-zinc-600 font-mono">
                      <span>50% (Standard Scale)</span>
                      <span>100% (Zero-Fund Mandate Target)</span>
                    </div>
                  </div>

                  {/* Slider 2: Placement Fee Allocation % */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-[9px] font-mono">
                      <span className="text-zinc-400 uppercase font-bold">Placement Fee Allocation %</span>
                      <span className="text-[#00ffd2] font-extrabold">{placementFeePct}% of Grant Allowance</span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="30"
                      value={placementFeePct}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setPlacementFeePct(val);
                        addLog(`COMPLIANCE: Calibrated agency Client Resource Workflow fee allocation to ${val}% total.`);
                      }}
                      className="w-full accent-[#00ffd2] bg-zinc-900 rounded-lg h-1.5 cursor-pointer"
                    />
                    <div className="flex justify-between text-[8px] text-zinc-600 font-mono">
                      <span>5% (Minimum Threshold)</span>
                      <span>30% (Premium Placement Cap)</span>
                    </div>
                  </div>
                </div>

                {/* Accounting Metrics Outputs */}
                <div className="grid grid-cols-2 gap-3.5 bg-black/60 border border-zinc-900 p-4 rounded-2xl">
                  <div>
                    <span className="text-zinc-500 uppercase font-mono text-[7px] block tracking-widest font-bold">CLIENT NET DIRECT LIABILITY</span>
                    <span className="text-white text-base font-black font-mono tracking-tight">
                      £{((35000 * (100 - approvedSubsidyPct)) / 100).toFixed(2)}
                    </span>
                    <span className="text-[8px] block text-emerald-400 font-mono uppercase font-bold mt-0.5">
                      {approvedSubsidyPct === 100 ? "✓ 100% Zero-Fund Complete" : "Subsidy Offset Active"}
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-500 uppercase font-mono text-[7px] block tracking-widest font-bold">ESTIMATED AGENCY PLACEMENT FEE</span>
                    <span className="text-amber-400 text-base font-black font-mono tracking-tight">
                      £{((35000 * placementFeePct) / 100).toFixed(2)}
                    </span>
                    <span className="text-[8px] block text-[#00ffd2] font-mono uppercase font-black tracking-widest mt-0.5">
                      Secure Payment Provider COV: 100% ABSORBABLE
                    </span>
                  </div>
                </div>

                {/* Visual Checklist */}
                <div className="space-y-2">
                  <div className="text-[9px] uppercase font-mono font-black text-zinc-400 tracking-wider">Acquisition Verification Badges</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[8px] font-mono">
                    <div className="flex items-center justify-between p-2 rounded-xl bg-zinc-950 border border-zinc-900 text-slate-300">
                      <span>Mutual NDA Signed</span>
                      <span className="text-emerald-400 font-black">ACTIVE</span>
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-xl bg-zinc-950 border border-zinc-900 text-slate-300">
                      <span>NexoraX Mail Created (@nexorax.co.uk)</span>
                      <span className="text-[#00ffd2] font-black">PROV_ON</span>
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-xl bg-zinc-950 border border-zinc-900 text-slate-300">
                      <span>NI NH448796B Validation Status</span>
                      <span className="text-emerald-400 font-black">VALIDATED</span>
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-xl bg-zinc-950 border border-zinc-900 text-slate-300">
                      <span>No-Win-No-Fee Covenant Status</span>
                      <span className="text-pink-400 font-black">COMPLIATED</span>
                    </div>
                  </div>
                </div>

                {/* Direct operating line trigger button */}
                <div className="p-3 bg-zinc-950 border border-pink-500/10 rounded-xl flex items-center justify-between gap-3 text-left">
                  <div className="font-mono text-[9px]">
                    <span className="text-zinc-500 uppercase block font-bold text-[7px]">DIRECTOR OPERATING HOTLINE</span>
                    <span className="text-white font-black">07359 062464</span>
                  </div>
                  <a
                    href="tel:07359062464"
                    className="px-3.5 py-1.5 bg-pink-500 text-white font-mono uppercase text-[9px] font-extrabold rounded-lg hover:bg-pink-600 transition-colors cursor-pointer tracking-wider shrink-0"
                  >
                    📞 Initiate Quick Call
                  </a>
                </div>

              </div>

            </motion.div>
          )}
        </ConsoleErrorBoundary>
      )}

    </div>
  );
}
